import React from 'react';
import { Link } from 'react-router-dom';
import { CreditCard as Edit, Play, Pause, ExternalLink, AlertTriangle, Bot as BotIcon } from 'lucide-react';
import type { Bot } from '@/utils/types';
import { formatValue } from '../../utils/formatValue';

interface ActiveBotsTableProps {
  bots: Bot[];
  loading: string | null;
  onToggleStatus: (botId: string) => void;
  onEditBot: (botId: string) => void;
}

export const ActiveBotsTable: React.FC<ActiveBotsTableProps> = ({ 
  bots, 
  loading, 
  onToggleStatus, 
  onEditBot 
}) => {
  // Add safety check for bots data
  const safeBots = bots || [];
  
  const getBotStatusColor = (bot: Bot) => {
    if (!bot.isConnected) {
      return {
        bg: 'bg-orange-100 dark:bg-orange-900/20',
        text: 'text-orange-800 dark:text-orange-200',
        label: 'Error'
      };
    }
    
    if (bot.isActive) {
      return {
        bg: 'bg-green-100 dark:bg-green-900/20',
        text: 'text-green-800 dark:text-green-200',
        label: 'Active'
      };
    }
    
    return {
      bg: 'bg-red-100 dark:bg-red-900/20',
      text: 'text-red-800 dark:text-red-200',
      label: 'Inactive'
    };
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700">
      <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">Your Active Bots</h2>
        <Link
          to="/active-bots"
          className="text-sm text-blue-500 hover:text-blue-700 dark:hover:text-blue-300"
        >
          View All
        </Link>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Bot Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Exchange
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Strategy
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Profit
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {safeBots.map((bot) => {
              const status = getBotStatusColor(bot);
              return (
                <tr key={bot.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-8 w-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                        <BotIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                      </div>
                      <span className="ml-3 text-sm font-medium text-gray-900 dark:text-white">
                        {formatValue(bot.name)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatValue(bot.exchange)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatValue(bot.strategy)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
                      {!bot.isConnected && <AlertTriangle className="w-3 h-3 mr-1" />}
                      {status.label}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-sm font-medium flex items-center gap-1 ${
                      (bot.profit || 0) >= 0 
                        ? 'text-green-600 dark:text-green-400' 
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      {(bot.profit || 0) >= 0 ? '+' : ''}{(bot.profit || 0).toFixed(2)}%
                     <span className="text-xs text-gray-500 dark:text-gray-400">{bot.lastExitReason && `(${formatValue(bot.lastExitReason)})`}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex justify-end space-x-2">
                      <Link 
                        to={`/bot/${bot.id}`}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Link>
                      <button 
                        onClick={() => onEditBot(bot.id)}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => onToggleStatus(bot.id)}
                        className={`${
                          bot.isActive 
                            ? 'text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300' 
                            : 'text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300'
                        }`}
                        disabled={loading === bot.id}
                      >
                        {loading === bot.id ? (
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : bot.isActive ? (
                          <Pause className="h-4 w-4" />
                        ) : (
                          <Play className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};